import { useEffect, useState } from "react";
import { AppShell, StatusBadge } from "@/components/AppShell";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BarChart, Bar, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Plus, Trash2, Ban, RotateCcw, Copy, Package, Users, KeySquare, Activity, Pencil, Upload, FileArchive, X, Eye, EyeOff, RefreshCw, KeyRound } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon }) {
  return (
    <Card className="bg-zinc-950 border-zinc-800 rounded-md p-5 hover:border-zinc-700 transition-colors">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon className="h-4 w-4 text-orange-500" />
      </div>
      <div className="text-4xl font-black tracking-tighter mt-3">{value}</div>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [plugins, setPlugins] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [customers, setCustomers] = useState([]);

  const emptyPlugin = { name: "", slug: "", description: "", version: "1.0.0", trial_days: 14, category: "", price: "", long_description: "", changelog: "", docs_url: "", status: "draft", cover_image_id: null, plugin_file_id: null, plugin_file_name: "", screenshot_ids: [] };
  const [pForm, setPForm] = useState(emptyPlugin);
  const [pOpen, setPOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState({ cover: false, file: false, shots: false });

  const [iForm, setIForm] = useState({ email: "", plugin_id: "", locking_id: "", mac_address: "", type: "full", days: "" });
  const [iOpen, setIOpen] = useState(false);

  const [licenseQuery, setLicenseQuery] = useState("");
  const [licenseStatusFilter, setLicenseStatusFilter] = useState("all");
  const [customerQuery, setCustomerQuery] = useState("");

  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [rotating, setRotating] = useState(false);

  const loadAll = async () => {
    const [s, p, l, c, sec] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/plugins"),
      api.get("/licenses"),
      api.get("/admin/customers"),
      api.get("/admin/secret"),
    ]);
    setStats(s.data);
    setPlugins(p.data);
    setLicenses(l.data);
    setCustomers(c.data);
    setSecret(sec.data.secret || "");
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openCreate = () => { setEditingId(null); setPForm(emptyPlugin); setPOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setPForm({
      name: p.name || "", slug: p.slug || "", description: p.description || "",
      version: p.version || "1.0.0", trial_days: p.trial_days ?? 14,
      category: p.category || "", price: p.price ?? "", long_description: p.long_description || "",
      changelog: p.changelog || "", docs_url: p.docs_url || "", status: p.status || "draft",
      cover_image_id: p.cover_image_id || null, plugin_file_id: p.plugin_file_id || null,
      plugin_file_name: p.plugin_file_name || "", screenshot_ids: p.screenshot_ids || [],
    });
    setPOpen(true);
  };

  const savePlugin = async () => {
    const payload = {
      ...pForm,
      trial_days: Number(pForm.trial_days) || 0,
      price: pForm.price === "" || pForm.price === null ? null : Number(pForm.price),
      tags: pForm.category ? [pForm.category.toLowerCase()] : [],
    };
    try {
      if (editingId) {
        await api.put(`/plugins/${editingId}`, payload);
        toast.success("Plugin updated");
      } else {
        await api.post("/plugins", payload);
        toast.success("Plugin created");
      }
      setPOpen(false);
      setPForm(emptyPlugin);
      setEditingId(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    const key = kind === "cover" ? "cover" : "file";
    setUploading((u) => ({ ...u, [key]: true }));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "cover") {
        setPForm((f) => ({ ...f, cover_image_id: data.id }));
        toast.success("Cover image uploaded");
      } else {
        setPForm((f) => ({ ...f, plugin_file_id: data.id, plugin_file_name: data.filename }));
        toast.success("Plugin file uploaded");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading((u) => ({ ...u, [key]: false }));
    }
  };

  const uploadScreenshots = async (files) => {
    if (!files || files.length === 0) return;
    setUploading((u) => ({ ...u, shots: true }));
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "screenshot");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPForm((f) => ({ ...f, screenshot_ids: [...f.screenshot_ids, data.id] }));
      }
      toast.success("Screenshot(s) uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading((u) => ({ ...u, shots: false }));
    }
  };

  const removeScreenshot = (sid) => {
    setPForm((f) => ({ ...f, screenshot_ids: f.screenshot_ids.filter((x) => x !== sid) }));
  };

  const deletePlugin = async (id) => {
    try {
      await api.delete(`/plugins/${id}`);
      toast.success("Plugin deleted");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const issueLicense = async () => {
    try {
      const payload = { ...iForm, days: iForm.days === "" ? null : Number(iForm.days) };
      await api.post("/licenses", payload);
      toast.success("License issued");
      setIOpen(false);
      setIForm({ email: "", plugin_id: "", locking_id: "", mac_address: "", type: "full", days: "" });
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/licenses/${id}/revoke`);
      toast.success("License revoked");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const reactivate = async (id) => {
    try {
      await api.post(`/licenses/${id}/reactivate`);
      toast.success("License reactivated");
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const rotateSecret = async () => {
    if (!window.confirm("Rotate the license secret and re-sign ALL existing licenses? Plugin builds using the previous secret will stop validating until you update them with the new secret.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/admin/rotate-secret");
      setSecret(data.new_secret);
      setShowSecret(true);
      toast.success(`Secret rotated · ${data.resigned} licenses re-signed`);
      await loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRotating(false);
    }
  };

  const filteredLicenses = licenses.filter((l) => {
    const q = licenseQuery.trim().toLowerCase();
    const matchesQ = !q || [l.plugin_name, l.email, l.locking_id, l.mac_address].some((v) => (v || "").toLowerCase().includes(q));
    const matchesStatus = licenseStatusFilter === "all" || l.status === licenseStatusFilter;
    return matchesQ && matchesStatus;
  });
  const filteredCustomers = customers.filter((c) => {
    const q = customerQuery.trim().toLowerCase();
    return !q || [c.name, c.email].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <AppShell
      title="Admin"
      subtitle="Manage plugins, issue and revoke licenses, and monitor your customer base."
    >
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Licenses" value={stats?.total_licenses ?? "—"} icon={KeySquare} />
        <Stat label="Active Trials" value={stats?.active_trials ?? "—"} icon={Activity} />
        <Stat label="Customers" value={stats?.customers ?? "—"} icon={Users} />
        <Stat label="Plugins" value={stats?.plugins ?? "—"} icon={Package} />
      </div>

      {stats?.recent && (
        <Card className="bg-zinc-950 border-zinc-800 rounded-md p-5 mb-10">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-4">
            Licenses issued · last 7 days
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={stats.recent}>
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#52525b" fontSize={11} fontFamily="JetBrains Mono" />
              <Tooltip
                cursor={{ fill: "#27272a" }}
                contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 6, fontFamily: "JetBrains Mono", fontSize: 12 }}
              />
              <Bar dataKey="count" fill="#f97316" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Tabs defaultValue="licenses">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <TabsTrigger value="licenses" data-testid="tab-licenses" className="rounded-sm data-[state=active]:bg-orange-600 data-[state=active]:text-white font-mono text-xs uppercase tracking-widest">Licenses</TabsTrigger>
          <TabsTrigger value="plugins" data-testid="tab-plugins" className="rounded-sm data-[state=active]:bg-orange-600 data-[state=active]:text-white font-mono text-xs uppercase tracking-widest">Plugins</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-sm data-[state=active]:bg-orange-600 data-[state=active]:text-white font-mono text-xs uppercase tracking-widest">Customers</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-sm data-[state=active]:bg-orange-600 data-[state=active]:text-white font-mono text-xs uppercase tracking-widest">Settings</TabsTrigger>
        </TabsList>

        {/* Licenses tab */}
        <TabsContent value="licenses" className="mt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
            <div className="flex gap-3 flex-1 max-w-xl">
              <Input data-testid="license-search-input" value={licenseQuery} onChange={(e) => setLicenseQuery(e.target.value)} placeholder="Search plugin, email, locking ID, MAC..." className="rounded-sm bg-zinc-900 border-zinc-800 font-mono text-sm" />
              <Select value={licenseStatusFilter} onValueChange={setLicenseStatusFilter}>
                <SelectTrigger data-testid="license-status-filter" className="w-40 rounded-sm bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={iOpen} onOpenChange={setIOpen}>
              <DialogTrigger asChild>
                <Button data-testid="issue-license-btn" className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white">
                  <Plus className="h-4 w-4 mr-1.5" /> Issue license
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800">
                <DialogHeader><DialogTitle>Issue license</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Customer email</Label>
                    <Input data-testid="issue-email-input" value={iForm.email} onChange={(e) => setIForm({ ...iForm, email: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" placeholder="customer@company.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Plugin</Label>
                    <Select value={iForm.plugin_id} onValueChange={(v) => setIForm({ ...iForm, plugin_id: v })}>
                      <SelectTrigger data-testid="issue-plugin-select" className="rounded-sm bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select plugin" /></SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {plugins.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Locking ID</Label>
                    <Input data-testid="issue-coreid-input" value={iForm.locking_id} onChange={(e) => setIForm({ ...iForm, locking_id: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" placeholder="QSC-CORE110F-..." />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">MAC Address</Label>
                    <Input data-testid="issue-mac-input" value={iForm.mac_address} onChange={(e) => setIForm({ ...iForm, mac_address: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" placeholder="00:1A:2B:3C:4D:5E" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Type</Label>
                      <Select value={iForm.type} onValueChange={(v) => setIForm({ ...iForm, type: v })}>
                        <SelectTrigger data-testid="issue-type-select" className="rounded-sm bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="full">Full</SelectItem>
                          <SelectItem value="trial">Trial</SelectItem>
                          <SelectItem value="subscription">Subscription</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Days (blank = perpetual)</Label>
                      <Input data-testid="issue-days-input" type="number" value={iForm.days} onChange={(e) => setIForm({ ...iForm, days: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" placeholder="365" />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="issue-submit-btn" onClick={issueLicense} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white">Sign & issue</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="border border-zinc-800 rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Plugin</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Email</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Locking ID</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Type</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Status</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLicenses.map((l) => (
                  <TableRow key={l.id} data-testid={`admin-license-row-${l.id}`} className="border-zinc-800">
                    <TableCell className="font-medium">{l.plugin_name}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{l.email}</TableCell>
                    <TableCell className="font-mono text-xs">{l.locking_id}</TableCell>
                    <TableCell className="font-mono text-xs uppercase">{l.type}</TableCell>
                    <TableCell><StatusBadge status={l.status} /></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" className="h-8 rounded-sm text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={() => copyText(l.token, "Token copied")} data-testid={`admin-copy-${l.id}`}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      {l.status === "revoked" ? (
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm text-emerald-400 hover:text-emerald-300 hover:bg-zinc-800" onClick={() => reactivate(l.id)} data-testid={`admin-reactivate-${l.id}`}>
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm text-red-400 hover:text-red-300 hover:bg-zinc-800" onClick={() => revoke(l.id)} data-testid={`admin-revoke-${l.id}`}>
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredLicenses.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 font-mono text-sm py-10">[ no matching licenses ]</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Plugins tab */}
        <TabsContent value="plugins" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button data-testid="add-plugin-btn" onClick={openCreate} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Add plugin
            </Button>
            <Dialog open={pOpen} onOpenChange={setPOpen}>
              <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editingId ? "Edit plugin" : "Add plugin"}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Name</Label>
                    <Input data-testid="plugin-name-input" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" placeholder="LifeLike Delay" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Slug</Label>
                      <Input data-testid="plugin-slug-input" value={pForm.slug} onChange={(e) => setPForm({ ...pForm, slug: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" placeholder="lifelike-delay" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Version</Label>
                      <Input data-testid="plugin-version-input" value={pForm.version} onChange={(e) => setPForm({ ...pForm, version: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Category</Label>
                      <Input data-testid="plugin-category-input" value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" placeholder="Reverb" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Price ($)</Label>
                      <Input data-testid="plugin-price-input" type="number" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" placeholder="199" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Trial days</Label>
                      <Input data-testid="plugin-trialdays-input" type="number" value={pForm.trial_days} onChange={(e) => setPForm({ ...pForm, trial_days: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Short description</Label>
                    <Input data-testid="plugin-desc-input" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" placeholder="One-line summary" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Long description</Label>
                    <Textarea data-testid="plugin-longdesc-input" value={pForm.long_description} onChange={(e) => setPForm({ ...pForm, long_description: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Changelog</Label>
                      <Input data-testid="plugin-changelog-input" value={pForm.changelog} onChange={(e) => setPForm({ ...pForm, changelog: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" placeholder="1.0.0 - Initial" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Docs URL</Label>
                      <Input data-testid="plugin-docs-input" value={pForm.docs_url} onChange={(e) => setPForm({ ...pForm, docs_url: e.target.value })} className="rounded-sm bg-zinc-900 border-zinc-800 font-mono" placeholder="https://" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Cover image</Label>
                      <label className="flex items-center gap-2 cursor-pointer rounded-sm bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-400 hover:border-zinc-700 transition-colors">
                        <Upload className="h-4 w-4" />
                        <span className="truncate">{uploading.cover ? "Uploading..." : pForm.cover_image_id ? "Change image" : "Upload image"}</span>
                        <input data-testid="plugin-cover-upload" type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset("cover", e.target.files[0])} />
                      </label>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Plugin file</Label>
                      <label className="flex items-center gap-2 cursor-pointer rounded-sm bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-400 hover:border-zinc-700 transition-colors">
                        <FileArchive className="h-4 w-4" />
                        <span className="truncate">{uploading.file ? "Uploading..." : pForm.plugin_file_name || "Upload .qplug/.zip"}</span>
                        <input data-testid="plugin-file-upload" type="file" accept=".qplug,.zip,.lua,application/zip" className="hidden" onChange={(e) => uploadAsset("file", e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Screenshots</Label>
                    <div className="flex flex-wrap gap-2">
                      {pForm.screenshot_ids.map((sid) => (
                        <div key={sid} className="relative group">
                          <img src={`${API}/files/${sid}`} alt="screenshot" className="h-16 w-24 object-cover rounded-sm border border-zinc-800" />
                          <button type="button" data-testid={`remove-screenshot-${sid}`} onClick={() => removeScreenshot(sid)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <label className="flex items-center justify-center h-16 w-24 cursor-pointer rounded-sm bg-zinc-900 border border-dashed border-zinc-700 text-zinc-500 hover:border-orange-500 hover:text-orange-400 transition-colors">
                        {uploading.shots ? <span className="text-[10px] font-mono">...</span> : <Plus className="h-5 w-5" />}
                        <input data-testid="plugin-screenshots-upload" type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadScreenshots(e.target.files)} />
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center justify-between border border-zinc-800 rounded-sm p-3">
                    <div>
                      <div className="text-sm font-medium">Published</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Visible to customers in marketplace</div>
                    </div>
                    <Switch data-testid="plugin-status-switch" checked={pForm.status === "published"} onCheckedChange={(v) => setPForm({ ...pForm, status: v ? "published" : "draft" })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="plugin-submit-btn" onClick={savePlugin} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white">{editingId ? "Save changes" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {plugins.map((p) => (
              <Card key={p.id} data-testid={`plugin-card-${p.slug}`} className="bg-zinc-950 border-zinc-800 rounded-md p-4 hover:border-zinc-700 transition-colors">
                {p.cover_image_id ? (
                  <img src={`${API}/files/${p.cover_image_id}`} alt={p.name} className="h-32 w-full object-cover rounded-sm mb-3 border border-zinc-800" />
                ) : (
                  <div className="h-32 w-full rounded-sm mb-3 border border-zinc-800 bg-zinc-900 flex items-center justify-center">
                    <Package className="h-8 w-8 text-zinc-700" />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-bold text-lg tracking-tight truncate">{p.name}</h3>
                    <p className="font-mono text-xs text-orange-500 mt-0.5 truncate">{p.slug} · v{p.version}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" className="h-8 rounded-sm text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={() => openEdit(p)} data-testid={`edit-plugin-${p.slug}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 rounded-sm text-red-400 hover:text-red-300 hover:bg-zinc-800" onClick={() => deletePlugin(p.id)} data-testid={`delete-plugin-${p.slug}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge className={`rounded-sm border text-[10px] font-mono uppercase tracking-widest ${p.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-zinc-500/15 text-zinc-400 border-zinc-500/30"}`}>{p.status || "draft"}</Badge>
                  {p.category && <Badge className="rounded-sm border border-zinc-700 bg-zinc-800 text-zinc-300 text-[10px] font-mono uppercase tracking-widest">{p.category}</Badge>}
                  {p.price != null && <span className="font-mono text-sm text-orange-400">${p.price}</span>}
                </div>
                <p className="text-sm text-zinc-400 mt-3 min-h-[40px]">{p.description}</p>
                <div className="font-mono text-xs text-zinc-500 mt-3 flex justify-between">
                  <span>TRIAL: {p.trial_days}d</span>
                  <span className={p.plugin_file_id ? "text-emerald-400" : "text-zinc-600"}>{p.plugin_file_id ? "FILE ✓" : "NO FILE"}</span>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Customers tab */}
        <TabsContent value="customers" className="mt-6">
          <div className="mb-4 max-w-lg">
            <Input data-testid="customer-search-input" value={customerQuery} onChange={(e) => setCustomerQuery(e.target.value)} placeholder="Search customers by name or email..." className="rounded-sm bg-zinc-900 border-zinc-800 font-mono text-sm" />
          </div>
          <div className="border border-zinc-800 rounded-md overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Name</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Email</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Licenses</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest">Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((c) => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className="border-zinc-800">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{c.email}</TableCell>
                    <TableCell className="font-mono">{c.license_count}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-400">{c.created_at?.slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
                {filteredCustomers.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-zinc-500 font-mono text-sm py-10">[ no matching customers ]</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        {/* Settings tab */}
        <TabsContent value="settings" className="mt-6">
          <Card className="bg-zinc-950 border-zinc-800 rounded-md p-6 max-w-2xl">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound className="h-5 w-5 text-orange-500" />
              <h2 className="text-xl font-bold tracking-tight">License signing secret</h2>
            </div>
            <p className="text-sm text-zinc-400 mb-5">HMAC-SHA256 shared secret. Embed this in your plugin builds so they can verify licenses offline. Keep it confidential.</p>
            <Label className="font-mono text-xs uppercase tracking-widest text-zinc-500">Active secret</Label>
            <div className="mt-1.5 bg-black border border-zinc-800 rounded-sm p-3 font-mono text-xs text-orange-400 break-all min-h-[44px]" data-testid="license-secret-value">
              {showSecret ? secret : "•".repeat(Math.min(secret.length, 64))}
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white" onClick={() => setShowSecret(!showSecret)} data-testid="toggle-secret-btn">
                {showSecret ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}{showSecret ? "Hide" : "Reveal"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-sm border-zinc-700 hover:bg-zinc-800 hover:text-white" onClick={() => copyText(secret, "Secret copied")} data-testid="copy-secret-btn">
                <Copy className="h-4 w-4 mr-1.5" /> Copy
              </Button>
            </div>
            <div className="border-t border-zinc-800 mt-6 pt-6">
              <h3 className="font-semibold">Rotate secret</h3>
              <p className="text-sm text-zinc-400 mt-1 mb-4">Generates a brand-new secret and re-signs every existing license with it. Update your plugin builds with the new secret afterwards, or older tokens will fail verification.</p>
              <Button onClick={rotateSecret} disabled={rotating} className="rounded-sm bg-orange-600 hover:bg-orange-700 text-white hover:text-white" data-testid="rotate-secret-btn">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${rotating ? "animate-spin" : ""}`} /> {rotating ? "Rotating..." : "Rotate & re-sign all"}
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
